import { Controller } from "@hotwired/stimulus"
import L from "leaflet"
import {
  formatCoordinate,
  infoMarkup,
  markerIconFor,
  markerTooltipFor,
  routeStyleFor
} from "../lib/shared/route_map/display_helpers"
import { fetchHtml, fetchJson } from "../lib/shared/route_map/request_helpers"

export default class extends Controller {
  static targets = [
    "addButton",
    "addressInput",
    "cancelButton",
    "canvas",
    "formPanel",
    "latitudeInput",
    "latitudePreview",
    "longitudeInput",
    "longitudePreview",
    "mapsUrlInput",
    "nameInput",
    "nearbyChoices",
    "nearbyChoicesPanel",
    "placeIdInput",
    "status",
    "statusMessage",
    "typeInput"
  ]

  static values = {
    addPromptText: String,
    attribution: String,
    defaultCenter: Array,
    defaultZoom: Number,
    editable: Boolean,
    lookupAppliedText: String,
    lookupEnabled: Boolean,
    lookupErrorText: String,
    lookupLoadingText: String,
    lookupNoResultsText: String,
    nearbyErrorText: String,
    nearbyLoadingText: String,
    nearbyLocationsPath: String,
    payload: Object,
    reverseLookupPath: String,
    selectionPromptText: String,
    tileUrl: String
  }

  connect() {
    if (!this.hasCanvasTarget) return

    if (!this.mapShouldRender()) return

    this.addMode = false
    this.lookupAbortController = null
    this.nearbyAbortController = null
    this.draftMarker = null
    this.onMapClick = this.handleMapClick.bind(this)

    this.map = L.map(this.canvasTarget, {
      scrollWheelZoom: false,
      zoomControl: true
    })

    L.tileLayer(this.tileUrlValue, {
      attribution: this.attributionValue,
      maxZoom: 19
    }).addTo(this.map)

    this.renderRoute()
    this.renderMarkers()
    this.fitBounds()

    if (this.editableValue) {
      this.map.on("click", this.onMapClick)
      this.restoreDraftState()
      this.syncEditableUi()
    }
  }

  disconnect() {
    this.abortLookupRequest()
    this.abortNearbyRequest()
    this.removeDraftMarker()

    if (this.map && this.onMapClick) {
      this.map.off("click", this.onMapClick)
    }

    if (this.map) {
      this.map.remove()
      this.map = null
    }
  }

  mapShouldRender() {
    const hasMarkers = Boolean(this.payloadValue?.markers?.length)
    const hasGeometry = Boolean(this.payloadValue?.geometry)

    return hasMarkers || hasGeometry || this.editableValue
  }

  fitBounds() {
    if (this.payloadValue.bounds?.length === 2) {
      this.map.fitBounds(this.payloadValue.bounds, { padding: [24, 24] })
      return
    }

    if (this.payloadValue.markers?.length === 1) {
      const marker = this.payloadValue.markers[0]
      this.map.setView([marker.latitude, marker.longitude], 15)
      return
    }

    if (this.editableValue) {
      this.map.setView(this.defaultCenter(), this.defaultZoom())
    }
  }

  renderMarkers() {
    this.payloadValue.markers?.forEach((marker) => {
      L.marker([marker.latitude, marker.longitude], {
        icon: markerIconFor(marker)
      })
        .addTo(this.map)
        .bindTooltip(markerTooltipFor(marker), {
          direction: "top",
          offset: [0, -18]
        })
    })
  }

  renderRoute() {
    if (!this.payloadValue.geometry) return

    L.geoJSON(this.payloadValue.geometry, {
      style: routeStyleFor(this.payloadValue.geometry_source)
    }).addTo(this.map)
  }

  enableAddMode(event) {
    event?.preventDefault()

    this.addMode = true
    this.syncEditableUi()
    this.showStatus(this.addPromptTextValue)
  }

  cancelAddMode(event) {
    event?.preventDefault()

    this.addMode = false
    this.clearDraftSelection()
    this.syncEditableUi()
    this.hideStatus()
  }

  handleMapClick(event) {
    if (!this.editableValue || !this.addMode) return

    this.selectCoordinates(event.latlng.lat, event.latlng.lng)
  }

  async selectCoordinates(latitude, longitude) {
    const coordinates = this.roundedCoordinates(latitude, longitude)

    this.setDraftCoordinates(coordinates)
    this.prepareDraftSelection(coordinates)

    if (this.hasNameInputTarget) {
      this.nameInputTarget.focus()
    }

    await Promise.allSettled(this.lookupTasksFor(coordinates))
  }

  clearDraftSelection() {
    this.abortLookupRequest()
    this.abortNearbyRequest()

    this.resetDraftInputs()

    this.updateCoordinatePreview(null, null)
    this.hideFormPanel()
    this.removeDraftMarker()
  }

  defaultCenter() {
    if (this.hasDefaultCenterValue && this.defaultCenterValue.length === 2) {
      return this.defaultCenterValue
    }

    return [52.0907, 5.1214]
  }

  defaultZoom() {
    return this.hasDefaultZoomValue ? this.defaultZoomValue : 13
  }

  hideFormPanel() {
    if (this.hasFormPanelTarget) {
      this.formPanelTarget.classList.add("hidden")
    }
  }

  hideStatus() {
    if (this.hasStatusTarget) {
      this.statusTarget.classList.add("hidden")
    }

    if (this.hasStatusMessageTarget) {
      this.statusMessageTarget.textContent = ""
    }
  }

  placeDraftMarker(latitude, longitude) {
    if (!this.map) return

    if (this.draftMarker) {
      this.draftMarker.setLatLng([latitude, longitude])
      return
    }

    this.draftMarker = L.circleMarker([latitude, longitude], {
      color: "#ea580c",
      fillColor: "#fb923c",
      fillOpacity: 0.95,
      radius: 8,
      weight: 3
    }).addTo(this.map)
  }

  async fetchReverseLookup(latitude, longitude) {
    this.abortLookupRequest()
    this.lookupAbortController = new AbortController()
    this.showStatus(this.lookupLoadingTextValue)

    try {
      const payload = await fetchJson(this.reverseLookupPathValue, {
        params: { latitude, longitude },
        signal: this.lookupAbortController.signal,
        errorText: this.lookupErrorTextValue
      })

      if (!payload.location) {
        this.showStatus(this.lookupNoResultsTextValue)
        return
      }

      this.applyLookupResult(payload.location)
      this.showStatus(this.lookupAppliedTextValue)
    } catch (error) {
      if (error.name === "AbortError") return

      console.error("Error loading reverse geocode result:", error)
      this.showStatus(error.message || this.lookupErrorTextValue)
    } finally {
      this.lookupAbortController = null
    }
  }

  async fetchNearbyLocations(latitude, longitude) {
    this.abortNearbyRequest()
    this.nearbyAbortController = new AbortController()
    this.showNearbyChoices(infoMarkup(this.nearbyLoadingTextValue))

    try {
      const html = await fetchHtml(this.nearbyLocationsPathValue, {
        params: { latitude, longitude },
        signal: this.nearbyAbortController.signal,
        errorText: this.nearbyErrorTextValue
      })

      this.showNearbyChoices(html)
    } catch (error) {
      if (error.name === "AbortError") return

      console.error("Error loading nearby route locations:", error)
      this.showNearbyChoices(infoMarkup(error.message || this.nearbyErrorTextValue, "amber"))
    } finally {
      this.nearbyAbortController = null
    }
  }

  applyLookupResult(location) {
    if (location.address && this.hasAddressInputTarget) {
      this.addressInputTarget.value = location.address
    }

    if (location.latitude && this.hasLatitudeInputTarget) {
      this.latitudeInputTarget.value = location.latitude
    }

    if (location.longitude && this.hasLongitudeInputTarget) {
      this.longitudeInputTarget.value = location.longitude
    }

    if (location.maps_url && this.hasMapsUrlInputTarget) {
      this.mapsUrlInputTarget.value = location.maps_url
    }

    if (location.place_id && this.hasPlaceIdInputTarget) {
      this.placeIdInputTarget.value = location.place_id
    }
  }

  resetLookupFields() {
    if (this.hasAddressInputTarget) {
      this.addressInputTarget.value = ""
    }

    if (this.hasPlaceIdInputTarget) {
      this.placeIdInputTarget.value = ""
    }

    if (this.hasMapsUrlInputTarget) {
      this.mapsUrlInputTarget.value = ""
    }
  }

  resetDraftInputs() {
    this.resetLookupFields()
    this.resetNearbyChoices()

    if (this.hasLatitudeInputTarget) {
      this.latitudeInputTarget.value = ""
    }

    if (this.hasLongitudeInputTarget) {
      this.longitudeInputTarget.value = ""
    }

    if (this.hasNameInputTarget) {
      this.nameInputTarget.value = ""
    }

    if (this.hasTypeInputTarget) {
      this.typeInputTarget.value = ""
    }
  }

  abortLookupRequest() {
    if (this.lookupAbortController) {
      this.lookupAbortController.abort()
      this.lookupAbortController = null
    }
  }

  abortNearbyRequest() {
    if (this.nearbyAbortController) {
      this.nearbyAbortController.abort()
      this.nearbyAbortController = null
    }
  }

  removeDraftMarker() {
    if (!this.draftMarker || !this.map) return

    this.map.removeLayer(this.draftMarker)
    this.draftMarker = null
  }

  restoreDraftState() {
    if (!this.editableValue) return

    const latitude = this.hasLatitudeInputTarget ? this.latitudeInputTarget.value : null
    const longitude = this.hasLongitudeInputTarget ? this.longitudeInputTarget.value : null

    if (!latitude || !longitude) {
      this.hideFormPanel()
      this.hideStatus()
      return
    }

    this.addMode = true
    this.placeDraftMarker(Number(latitude), Number(longitude))
    this.showFormPanel()
    this.updateCoordinatePreview(Number(latitude), Number(longitude))
    this.showStatus(this.selectionPromptTextValue)
  }

  roundedCoordinates(latitude, longitude) {
    return {
      latitude: Number(latitude).toFixed(6),
      longitude: Number(longitude).toFixed(6)
    }
  }

  setDraftCoordinates({ latitude, longitude }) {
    if (this.hasLatitudeInputTarget) {
      this.latitudeInputTarget.value = latitude
    }

    if (this.hasLongitudeInputTarget) {
      this.longitudeInputTarget.value = longitude
    }
  }

  prepareDraftSelection({ latitude, longitude }) {
    const latitudeNumber = Number(latitude)
    const longitudeNumber = Number(longitude)

    this.placeDraftMarker(latitudeNumber, longitudeNumber)
    this.showFormPanel()
    this.updateCoordinatePreview(latitudeNumber, longitudeNumber)
    this.showStatus(this.selectionPromptTextValue)
    this.resetLookupFields()
    this.resetNearbyChoices()
  }

  lookupTasksFor({ latitude, longitude }) {
    const tasks = []

    if (this.lookupEnabledValue && this.reverseLookupPathValue) {
      tasks.push(this.fetchReverseLookup(latitude, longitude))
    }

    if (this.nearbyLocationsPathValue) {
      tasks.push(this.fetchNearbyLocations(latitude, longitude))
    }

    return tasks
  }

  showNearbyChoices(html) {
    if (!this.hasNearbyChoicesTarget || !this.hasNearbyChoicesPanelTarget) return

    this.nearbyChoicesTarget.innerHTML = html
    this.nearbyChoicesPanelTarget.classList.remove("hidden")
  }

  resetNearbyChoices() {
    if (this.hasNearbyChoicesTarget) {
      this.nearbyChoicesTarget.innerHTML = ""
    }

    if (this.hasNearbyChoicesPanelTarget) {
      this.nearbyChoicesPanelTarget.classList.add("hidden")
    }
  }

  showFormPanel() {
    if (this.hasFormPanelTarget) {
      this.formPanelTarget.classList.remove("hidden")
    }
  }

  showStatus(message) {
    if (!this.hasStatusTarget) return

    if (this.hasStatusMessageTarget) {
      this.statusMessageTarget.textContent = message
    }

    this.statusTarget.classList.remove("hidden")
  }

  syncEditableUi() {
    if (!this.editableValue) return

    if (this.hasAddButtonTarget) {
      this.addButtonTarget.classList.toggle("hidden", this.addMode)
    }

    if (this.hasCancelButtonTarget) {
      this.cancelButtonTarget.classList.toggle("hidden", !this.addMode)
      this.cancelButtonTarget.classList.toggle("inline-flex", this.addMode)
    }
  }

  updateCoordinatePreview(latitude, longitude) {
    const displayLatitude = formatCoordinate(latitude)
    const displayLongitude = formatCoordinate(longitude)

    if (this.hasLatitudePreviewTarget) {
      this.latitudePreviewTarget.textContent = displayLatitude
    }

    if (this.hasLongitudePreviewTarget) {
      this.longitudePreviewTarget.textContent = displayLongitude
    }
  }
}